import { Theme, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { TotalVehiclesWidgetDto } from "../../../../../common/data/service-proxies";
import { StackedCircularIndicator } from "./StackedCircularIndicator";

interface Props {
  total: TotalVehiclesWidgetDto | undefined;
}

const getElementHeight = (element: HTMLElement) => {
  const computedStyle = getComputedStyle(element);
  let height = element.clientHeight;
  const paddingTop = parseFloat(computedStyle.paddingTop);
  const paddingBottom = parseFloat(computedStyle.paddingBottom);
  height -= paddingTop + paddingBottom;
  return height;
};

export const TotalVehiclesCircularIndicators = (props: Props) => {
  const { total } = props;
  const [chartElementSize, setChartElementSize] = useState<number>(192);
  const chartElementRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const theme = useTheme<Theme>();
  const handleWindowResize = useCallback(() => {
    if (chartElementRef.current) {
      if (window.innerWidth > 500) {
        setChartElementSize(getElementHeight(chartElementRef.current));
        chartElementRef.current.style.setProperty(
          "flex-basis",
          `${chartElementRef.current.clientHeight}px`
        );
      } else {
        setChartElementSize(200);
        chartElementRef.current.style.setProperty(
          "flex-basis",
          `700px`
        );
      }
    }
  }, []);

  useEffect(() => {
    if (labelRef.current) {
      const left =
        (chartElementSize + 100) / 2 - labelRef.current.clientWidth / 2;
      labelRef.current.style.left = `${left}px`;
    }
  }, [chartElementSize]);

  useEffect(() => {
    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [handleWindowResize]);

  return (
    <div className="total-vehicles-chart" ref={chartElementRef}>
      <StackedCircularIndicator
        ringNumber={1}
        wrapperSize={chartElementSize}
        value={total?.vehiclesWithDiagnosticErrorsPercentage}
        color={theme.palette.error.main}
      />
      <StackedCircularIndicator
        ringNumber={2}
        wrapperSize={chartElementSize}
        value={total?.vehiclesWithCriticalGeometrisEventsPercentage}
        color={theme.palette.warning.main}
      />
      <StackedCircularIndicator
        ringNumber={3}
        wrapperSize={chartElementSize}
        value={total?.vehiclesWithTpmsAlertsPercentage}
        color={theme.palette.error.main}
      />
      <StackedCircularIndicator
        ringNumber={4}
        wrapperSize={chartElementSize}
        value={total?.vehiclesWithUnresolvedDvirDefectsPercentage}
        color={theme.palette.warning.main}
      />
      <div className="total-vehicles-label" ref={labelRef}>
        <div className="total-vehicles-count">
          <Typography variant="h4">{total?.activeVehicles ?? 0}</Typography>
        </div>
        <div className="total-vehicles-description">
          <Typography variant="body2">Vehicles</Typography>
        </div>
      </div>
    </div>
  );
};
